<template>
    <a v-if="href" :href="href" :class="classes" :target="openInNewWindow">
        <figure v-if="icon">
            <span v-if="isMaterialIcon" :class="materialIconType">{{ materialIconName }}</span>
            <svg-icon v-else :icon="icon" />
        </figure>
        <span class="v-btn--label" v-html="label"></span>
    </a>
    <button
        v-else
        :type="buttonType"
        :class="classes"
        :data-target="modalTarget && modalTarget !== 'dismiss' ? '#'+modalTarget : false"
        :data-toggle="modalTarget && modalTarget !== 'dismiss' ? 'modal' : false"
        :data-dismiss="modalTarget ? 'modal' : false"
        @click="onClick"
    >
        <figure class="spinner" v-if="isSending">
            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="spinner" class="svg-inline--fa fa-spinner fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M304 48c0 26.51-21.49 48-48 48s-48-21.49-48-48 21.49-48 48-48 48 21.49 48 48zm-48 368c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zm208-208c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zM96 256c0-26.51-21.49-48-48-48S0 229.49 0 256s21.49 48 48 48 48-21.49 48-48zm12.922 99.078c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.491-48-48-48zm294.156 0c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.49-48-48-48zM108.922 60.922c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.491-48-48-48z"></path></svg>
        </figure>
        <figure v-if="icon">
            <span v-if="isMaterialIcon" :class="materialIconType">{{ materialIconName }}</span>
            <svg-icon v-else :icon="icon" />
        </figure>
        <span v-if="label" class="v-btn--label" v-html="label"></span>
        <span v-if="type === 'datepicker'" class="material-symbols-rounded">expand_more</span>
    </button>
</template>

<script>

export default {
    props: {
        label: {
            type: String,
            required: false,
            default: ''
        },
        variant: {
            type: String,
            default: 'primary',
            validator: function (value) {
                return ['primary', 'secondary', 'ghost'].indexOf(value) !== -1;
            },
        },
        color: {
            type: String,
            default: 'default',
            validator: function (value) {
                return ['default', 'green', 'danger', 'white', 'dark'].indexOf(value) !== -1;
            },
        },
        type: {
            type: String,
            default: 'button',
            validator(value) {
                return ['button', 'submit', 'datepicker'].indexOf(value) !== -1
            }
        },
        size: {
            type: String,
            default: 'md',
            validator(value) {
                return ['xs', 'sm', 'md', 'lg'].indexOf(value) !== -1
            }
        },
        position: {
            type: String,
            default: 'left',
            validator(value) {
                return ['left', 'right', 'center'].indexOf(value) !== -1
            }
        },
        positionPhone: {
            type: String,
            default: 'left',
            validator: function(value) {
                return ['left', 'right', 'center'].indexOf(value) !== -1
            }
        },
        positionTablet: {
            type: String,
            default: 'left',
            validator: function(value) {
                return ['left', 'right', 'center'].indexOf(value) !== -1
            }
        },
        positionDesktop: {
            type: String,
            default: 'left',
            validator: function(value) {
                return ['left', 'right', 'center'].indexOf(value) !== -1
            }
        },
        icon: {
            type: String
        },
        href: {
            type: String
        },
        modalTarget: {
            type: String
        },
        disabled: {
            type: Boolean,
            default: false
        },
        newWindow: {
            type: Boolean,
            default: false
        },
        fullWidth: {
            type: Boolean,
            default: false
        },
        fullWidthMobile: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isSending: false,
            isDisabled: this.disabled
        }
    },
    computed: {
        classes() {
            return {
                'v-btn': true,
                [`v-btn--${this.variant}`]: true,
                [`v-btn--${this.variant}-${this.color}`]: true,
                [`v-btn--${this.size}`]: true,
                [`v-btn--${this.position}`]: this.position !== 'left',
                [`v-btn--phone-${this.positionPhone}`]: this.positionPhone !== 'left',
                [`v-btn--tablet-${this.positionTablet}`]: this.positionTablet !== 'left',
                [`v-btn--desktop-${this.positionDesktop}`]: this.positionDesktop !== 'left',
                [`v-btn--${this.type}`]: this.type === 'datepicker',
                'v-btn--disabled': this.isDisabled,
                'v-btn--full-width': this.fullWidth,
                'v-btn--full-width-mobile': this.fullWidthMobile,
                'v-btn--icon-only': !this.label,
                'v-btn--sending': this.isSending
            }
        },
        buttonType() {
            return this.type === 'submit' ? 'submit' : 'button'
        },
        openInNewWindow() {
            return this.newWindow ? '_blank' : false
        },
        isMaterialIcon() {
            return this.icon.startsWith('material-');
        },
        materialIconType() {
        if (this.isMaterialIcon && this.icon.includes('.')) {
            return this.icon.split('.')[0];
        }
            return '';
        },
        materialIconName() {
            if (this.isMaterialIcon && this.icon.includes('.')) {
                return this.icon.split('.')[1];
            }
            return '';
        }
    },
    watch: {
        disabled(disabled) {
            this.isDisabled = disabled
        }
    },
    mounted() {
        Vuemit.listen('Button--sending-failed', this.onSendingFailed)
    },
    methods: {
        async onClick() {
            if (this.type === 'submit' && this.modalTarget !== 'dismiss') {
                this.isSending = true
                this.isDisabled = true
            }

            this.$emit('onClick')
        },
        onSendingFailed() {
            this.isSending = false
            if(!this.disabled) {
                this.isDisabled = false
            }
        }
    }
}
</script>
